import React, { useState } from "react";

type Stage = "shrink" | "reveal" | "done";

interface LoginRevealTransitionProps {
  /** Play the transition (sign-in via the form); false renders children plainly. */
  play: boolean;
  /**
   * The signed-in login screen, kept mounted while its shrink-to-circle
   * animation finishes; it must call the handed-in callback when done.
   */
  renderLoginScreen: (onTransitionDone: () => void) => React.ReactNode;
  children: React.ReactNode;
}

/**
 * The login-to-admin handover animation: hold the login screen while its card
 * shrinks to a circle, then iris-reveal the admin panel with a clip-path.
 * Purely visual — the auth state machine (use-admin-auth) knows nothing of it.
 */
export function LoginRevealTransition({ play, renderLoginScreen, children }: LoginRevealTransitionProps) {
  const [stage, setStage] = useState<Stage>(play ? "shrink" : "done");

  if (stage === "shrink") {
    return <>{renderLoginScreen(() => setStage("reveal"))}</>;
  }

  const revealing = stage === "reveal";
  const handleAnimationEnd = (e: React.AnimationEvent) => {
    // Only react to the clip-path animation on the wrapper itself
    if (e.target === e.currentTarget) setStage("done");
  };

  return (
    <>
      {revealing && <div className="reveal-backdrop" />}
      <div
        className={revealing ? "reveal-clip" : undefined}
        onAnimationEnd={revealing ? handleAnimationEnd : undefined}
      >
        {children}
      </div>
      {revealing && <style>{revealStyles}</style>}
    </>
  );
}

/* ─── Reveal animation styles ──────────────────────────────────────── */
const revealStyles = /* css */ `
  /* Dark backdrop behind the clipped admin panel */
  .reveal-backdrop {
    position: fixed;
    inset: 0;
    background: oklch(0.16 0.02 260);
    z-index: 9998;
  }

  /* Admin content with expanding circle clip */
  .reveal-clip {
    position: relative;
    z-index: 9999;
    background: var(--color-background);
    clip-path: circle(2rem at 50% 50%);
    animation: reveal-iris 0.7s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }

  @keyframes reveal-iris {
    to {
      clip-path: circle(100vmax at 50% 50%);
    }
  }
`;
